const THEME_STORAGE_KEY = "nodeos-theme";
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";
/**
 * The browser adapter behind `ThemeEnvironmentPort` — the composition root
 * (`system/pwa/src/main.ts`) constructs this instead of `client/ui/src/shell/theme-init.ts`
 * touching `localStorage`/`matchMedia` inline, keeping browser I/O out of `client/ui`
 * (boundary rules B2/B3) the same way `BrowserClock`/`BrowserId` keep `setTimeout`/
 * `crypto.randomUUID` out of it. See `ThemeEnvironmentPort`'s own doc comment for why this
 * can't instead be reached through the ordinary `@econ-v1/rpc`/kernel path.
 */
export class BrowserThemeEnvironment {
    #storage;
    #matchMedia;
    constructor(options = {}) {
        this.#storage = options.storage ?? safeGlobalLocalStorage();
        this.#matchMedia = options.matchMedia ??
            (typeof globalThis.matchMedia === "function" ? globalThis.matchMedia.bind(globalThis) : undefined);
    }
    getStoredTheme() {
        try {
            const raw = this.#storage?.getItem(THEME_STORAGE_KEY);
            return raw === "dark" || raw === "light" ? raw : undefined;
        }
        catch {
            // Storage inaccessible (private browsing, disabled) — fall through to prefers-
            // color-scheme, same as the original skin's own theme-init.js try/catch.
            return undefined;
        }
    }
    setStoredTheme(preference) {
        try {
            this.#storage?.setItem(THEME_STORAGE_KEY, preference);
        }
        catch {
            // Storage unavailable (private browsing, quota) — still applied for this session by
            // the caller; the original skin's own theme-init.js swallows the same failure.
        }
    }
    prefersDarkColorScheme() {
        if (!this.#matchMedia)
            return false;
        try {
            return this.#matchMedia(DARK_MEDIA_QUERY).matches;
        }
        catch {
            return false;
        }
    }
    watchDarkColorScheme(onChange) {
        if (!this.#matchMedia)
            return undefined;
        let mql;
        try {
            mql = this.#matchMedia(DARK_MEDIA_QUERY);
        }
        catch {
            return undefined;
        }
        const listener = (event) => onChange(event.matches);
        mql.addEventListener("change", listener);
        return () => mql.removeEventListener("change", listener);
    }
}
export function createBrowserThemeEnvironment(options = {}) {
    return new BrowserThemeEnvironment(options);
}
function safeGlobalLocalStorage() {
    try {
        return globalThis.localStorage;
    }
    catch {
        // Accessing the property itself can throw in some locked-down embedders.
        return undefined;
    }
}
//# sourceMappingURL=browser-theme-environment.js.map